import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CellEditingStartedEvent, GridApi } from "ag-grid-community";

/**
 * Makes sure an AG Grid cell that is still being edited is committed before a
 * bulk save, so users don't have to press Enter or click away first.
 *
 * - Spread `gridEditProps` onto the grid: it stops editing when the grid loses
 *   focus and tracks whether a cell editor is open.
 * - Use `isCellEditing` alongside the page's dirty flag to enable Save, since
 *   an open editor's value has not reached `onCellValueChanged` yet.
 * - Call `requestSave()` from the Save button instead of the save handler.
 *   It closes any open editor and runs `onSave` once the committed value has
 *   reached page state.
 *
 * AG Grid delivers `cellValueChanged` before `cellEditingStopped` (both
 * asynchronously, in order), so by the time editing has stopped the row update
 * is already queued and `onSave` runs on the render that includes it.
 */
export function useGridEditCommit(onSave: () => void | Promise<void>) {
  const [isCellEditing, setIsCellEditing] = useState(false);
  const [saveRequest, setSaveRequest] = useState(0);
  // Refs (not state) so requestSave sees editor changes that haven't rendered yet
  const editingRef = useRef(false);
  const apiRef = useRef<GridApi | null>(null);
  const savePendingRef = useRef(false);
  const onSaveRef = useRef(onSave);

  useLayoutEffect(() => {
    onSaveRef.current = onSave;
  });

  useEffect(() => {
    if (saveRequest === 0) return;
    void onSaveRef.current();
  }, [saveRequest]);

  const onCellEditingStarted = useCallback((event: CellEditingStartedEvent) => {
    apiRef.current = event.api;
    editingRef.current = true;
    setIsCellEditing(true);
  }, []);

  const onCellEditingStopped = useCallback(() => {
    editingRef.current = false;
    setIsCellEditing(false);
    if (savePendingRef.current) {
      savePendingRef.current = false;
      setSaveRequest((n) => n + 1);
    }
  }, []);

  const requestSave = useCallback(() => {
    const api = apiRef.current;
    if (editingRef.current && api && !api.isDestroyed()) {
      savePendingRef.current = true;
      api.stopEditing();
      return;
    }
    setSaveRequest((n) => n + 1);
  }, []);

  return {
    isCellEditing,
    requestSave,
    gridEditProps: {
      stopEditingWhenCellsLoseFocus: true,
      onCellEditingStarted,
      onCellEditingStopped,
    },
  };
}
